import React, { ReactNode, useState, useRef, useEffect } from 'react';
import produce from "immer";
import cn from 'classnames';
import { Overlay, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './Devices.scss';
import { ErrorMessage, Loader, Page, PageHeader } from 'components';
import { ErrorType, DeviceLoadState, LoadState } from 'enums';
import { IDevice, IDeviceQuery, IMruDevice } from 'interfaces';
import { DevicesItem } from './DevicesItem';
import { DeviceModal } from './DeviceModal';
import { DevicesService, ManufacturersService, MruService, PingService, parseAxiosError } from 'services';
import { Button, ButtonGroup, Modal, ModalBody, ModalFooter, ModalHeader, TextField } from '@cfa/react-components';

interface IDevicesPageState {
    // device list
    deviceLoadStatus: DeviceLoadState;
    deviceLoadError?: string;
    // select
    selectedDevices?: IDevice[];
    // device modal
    viewDevice?: IDevice;
    showDeviceModal?: boolean;
}

const pageSize = 100;
export function Devices() {

    const [pageState, setPageState] = useState<IDevicesPageState>({
        deviceLoadStatus: DeviceLoadState.Initial,
        selectedDevices: [],
    });
    const { deviceLoadStatus, deviceLoadError, selectedDevices, viewDevice, showDeviceModal } = pageState;
    const [devices, setDevices] = useState<IDevice[]>(DevicesService.devicesPageCache);

    // maintain devices cache for navigating to a different component
    useEffect(() => {
        DevicesService.devicesPageCache = devices;
    }, [devices])

    // search button and popover
    const searchButtonRef = useRef(null);
    const [searchOpen, setSearchOpen] = useState(false);

    // mru button and popover
    const mruButtonRef = useRef(null);
    const [mruOpen, setMruOpen] = useState(false);

    // sorters
    const [activeSorter, setActiveSorter] = useState("lastSeen");
    const [descending, setDescending] = useState(true);

    // form search values
    const [location, setLocation] = useState('');
    const [manufacturer, setManufacturer] = useState('');
    const [modelNumber, setModelNumber] = useState('');
    const [serialNumber, setSerialNumber] = useState('');
    const [thingID, setThingID] = useState('');

    // select all
    const [allSelected, setAllSelected] = useState(false);

    // ping
    const [pingDisabled, setPingDisabled] = useState(false);

    // Search
    const onChangeManufacturer = (e) => {
        setManufacturer(e.target.value);
    }

    const onChangeLocation = (e) => {
        setLocation(e.target.value);
    }

    const onChangeModelNumber = (e) => {
        setModelNumber(e.target.value);
    }

    const onChangeSerialNumber = (e) => {
        setSerialNumber(e.target.value);
    }

    const onChangeThingID = (e) => {
        setThingID(e.target.value);
    }

    const onKeypress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            onSubmitSearchValues();
        }
    }

    const onSubmitSearchValues = () => {

        if (manufacturer == "" && location == "" && modelNumber == "" && serialNumber == "" && thingID == "") return;

        setSearchOpen(false);
        setAllSelected(false);

        setDevices([]);
        setPageState(
            produce((draft) => {
                draft.deviceLoadStatus = DeviceLoadState.Loading;
                draft.selectedDevices = [];
            })
        );

        let query: IDeviceQuery = {
            thingID: thingID,
            manufacturerName: manufacturer,
            modelNumber: modelNumber,
            locationNumber: location,
            serialNumber: serialNumber,
            offset: 0,
            limit: pageSize, 
            sort: getDataApiSorter(activeSorter, descending),
        }
   
        DevicesService.searchDevices(query).then((devs) => {
            doSuccess(devs);
        }).catch((err) => {
            // handle 404 composite query
            if (err.response.status == 404) {
                doSuccess([]);
                return;
            }
            setPageState(
                produce((draft) => {
                    draft.deviceLoadStatus = DeviceLoadState.Error;
                    draft.deviceLoadError = parseAxiosError(err);
                })
            );
        });
    };

    const doSuccess = (devs) => {
        setDevices(devs);
        setPageState(
            produce((draft) => {
                draft.deviceLoadStatus = DeviceLoadState.Success; 
            })
        );
    };

    function getDataApiSorter(sorter: string, descending: boolean): string {
        if(descending) {
            sorter = sorter + "Desc"
        }
        return sorter
    }

    // Sorts a function using the passed sorter. This requires refetching all devices from data api.
    // If all devices are already in memory the sortFunc is used instead of a call to data api
    const sortBy = (newSorter: string, sortFunc:  (a: IDevice, b: IDevice) => number) => {
        let newDescending =  descending
        if (activeSorter == newSorter) {
            newDescending = !newDescending
        } else {
            newDescending = true
        }
        setActiveSorter(newSorter);
        setDescending(newDescending)

        if (activeSorter != newSorter) {
            setDevices(devices.slice().sort(sortFunc));
        } else {
            setDevices(devices.slice().reverse());
        }
    }

    const collator = new Intl.Collator(undefined, {numeric: true, sensitivity: 'base'});
    const sortDevicesByManufacturer = () => sortBy("manufacturer", (a, b) => collator.compare(a.manufacturer.name + a.modelNumber, b.manufacturer.name + b.modelNumber));
    const sortDevicesByLastSeen = () => sortBy("lastSeen", (a, b) => b.lastSeen.getTime() - a.lastSeen.getTime());

    // Device select
    const isSelected = input => selectedDevices.some(({id}) => id == input);
    
    const onToggleSelectAll = () => {
        let newSelectedDevices;
        
        if (allSelected) newSelectedDevices = [];
        else newSelectedDevices = devices.slice();

        setAllSelected(!allSelected);
        setPageState(produce(draft => {draft.selectedDevices = newSelectedDevices}))
    }

    const onSelectDevice = (d: IDevice) => {
        let newSelectedDevices = selectedDevices.slice();

        if (isSelected(d.id)) {
            const deviceIndex = newSelectedDevices.findIndex(({id}) => id == d.id);
            newSelectedDevices.splice(deviceIndex, 1);
        }
        else newSelectedDevices.push(d);

        if (newSelectedDevices.length == devices.length) setAllSelected(true);
        else setAllSelected(false)

        setPageState(produce(draft => {draft.selectedDevices = newSelectedDevices}))
    }

    // Ping devices
    const deviceItemClassName = (deviceID: number) => {
        if (!devices) {
            return "devicesItem";
        }
        let dev = devices.find(d => d.id == deviceID);
        return cn("devicesItem", {
            'pingStatus_loading': dev.pingStatus === LoadState.Loading,
            'pingStatus_success': dev.pingStatus === LoadState.Success,
            'pingStatus_error': dev.pingStatus === LoadState.Error,
        });
    }

    const setDeviceStates = (devicesToChange: IDevice[], state: LoadState) => {
        if (!devicesToChange) return;
        // pass lambda to hook to prevent state race condition on consecutive calls
        setDevices(ds => ds.slice().map<IDevice>(device => ({
                ...device, 
                pingStatus: devicesToChange.find(d => d.id == device.id)? state : device.pingStatus
            })
       ));
    }

    const pingDevices = (devicesToPing: IDevice[]) => {
        if (devicesToPing.length == 0) return;

        setPingDisabled(true);
        setDeviceStates(devicesToPing, LoadState.Loading);

        PingService.pingDevices(devicesToPing)
            .then((resp) => {
                setDeviceStates(resp.fail, LoadState.Error);
                setDeviceStates(resp.success, LoadState.Success);
                setPingDisabled(false);
            })
            .catch((e) => {
                setDeviceStates(devicesToPing, LoadState.Error);
                setPingDisabled(false);
            });
    }

    // Device modal
    const openDeviceModal = (d: IDevice) => {
        setPageState(produce(draft => {
            draft.viewDevice = d;
            draft.showDeviceModal = true;
        }))
    }

    const closeDeviceModal = () => {
        setPageState(produce(draft => {
            draft.showDeviceModal = false;
        }))
    }

    const clearDevices = () => {
        setDevices([]);
        setThingID('');
        setManufacturer('');
        setSerialNumber('');
        setLocation('');
        setPageState(produce((draft) => {
            draft.deviceLoadStatus = DeviceLoadState.Success;
            draft.selectedDevices = [];
        }));
    }

    const setDevicesFromMru = (d: IMruDevice) => {
        setMruOpen(false);
        setDevices([]);
        setPageState(produce((draft) => {
            draft.deviceLoadStatus = DeviceLoadState.Loading;
            draft.selectedDevices = [];
        }));
        DevicesService.getDevices(d.thingIDs).then((ts) => {
            setDevices(ts);
            setPageState(produce((draft) => {
                draft.deviceLoadStatus = DeviceLoadState.Success;
            }));
        });
    }

    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkDeviceSearch, setBulkDeviceSearch] = useState("");
    const searchModal =
        <Modal
            disableOverlayClick
            show={showBulkModal}
            onClose={() => setShowBulkModal(false)}
        >
            <ModalHeader>Bulk Device Search</ModalHeader>
            <ModalBody>
                <TextField multiline={true} rows={10} fullWidth
                    placeholder="Space or comma separated list of ThingIDs" 
                    onChange={(e) => setBulkDeviceSearch(e.target.value)}>
                </TextField>
            </ModalBody>
            <ModalFooter>
                <Button
                    onClick={(e) => {
                        setShowBulkModal(false);
                        let newThingIDs = bulkDeviceSearch.split(/\s*[,\s]\s*/);
                        DevicesService.getDevices(newThingIDs).then((ts) => {
                            setDevices(ts);
                        });
                    }}
                >
                    Search
                </Button>
            </ModalFooter>
        </Modal>

    // Content
    const searchHeader: ReactNode = (
        <div className="devicesList_search">
            <ButtonGroup size='md'>

                <Button onClick={onSubmitSearchValues}>
                    <FontAwesomeIcon icon="redo"/>
                </Button>
                
                <Button onClick={() => setSearchOpen(!searchOpen)} ref={searchButtonRef} className="iconButtonForHeader">
                    <FontAwesomeIcon icon="search"/>
                </Button>

                <Overlay
                    target={searchButtonRef}
                    show={searchOpen}
                    onHide={() => setSearchOpen(false)}
                    rootClose
                    placement="bottom"
                >
                    <Popover id="deviceSearch_popover" className="searchPopover">
                        <Popover.Content>
                            <div className="searchPopover_group">
                                <div className="searchPopover_label">
                                    {'Manufacturer'}
                                </div>
                                <div className="searchPopover_value">
                                    <div className="inputWithButton">
                                        <select value={manufacturer} onChange={onChangeManufacturer}>
                                            {ManufacturersService.manufacturerNames.map((option) => (
                                                <option key={option} value={option}>
                                                    {option}
                                                </option>
                                            ))}
                                        </select>
                                        <button type="button" onClick={onSubmitSearchValues}>
                                            <FontAwesomeIcon icon="angle-right" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="searchPopover_group">
                                <div className="searchPopover_label">
                                    {'Model Number'}
                                </div>
                                <div className="searchPopover_value">
                                    <div className="inputWithButton">
                                        <input
                                            type="text"
                                            value={modelNumber}
                                            onChange={onChangeModelNumber}
                                            onKeyPress={onKeypress}
                                            maxLength={128}
                                        />
                                        <button type="button" onClick={onSubmitSearchValues}>
                                            <FontAwesomeIcon icon="angle-right" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="searchPopover_group">
                                <div className="searchPopover_label">
                                    {'Location'}
                                </div>
                                <div className="searchPopover_value">
                                    <div className="inputWithButton">
                                        <input
                                            type="text"
                                            value={location}
                                            onChange={onChangeLocation}
                                            onKeyPress={onKeypress}
                                            maxLength={128}
                                        />
                                        <button type="button" onClick={onSubmitSearchValues}>
                                            <FontAwesomeIcon icon="angle-right" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="searchPopover_group">
                                <div className="searchPopover_label">
                                    {'Serial Number'}
                                </div>
                                <div className="searchPopover_value">
                                    <div className="inputWithButton">
                                        <input
                                            type="text"
                                            value={serialNumber}
                                            onChange={onChangeSerialNumber}
                                            onKeyPress={onKeypress}
                                            maxLength={128}
                                        />
                                        <button type="button" onClick={onSubmitSearchValues}>
                                            <FontAwesomeIcon icon="angle-right" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="searchPopover_group">
                                <div className="searchPopover_label">
                                    {'ThingID'}
                                </div>
                                <div className="searchPopover_value">
                                    <div className="inputWithButton">
                                        <input
                                            type="text"
                                            value={thingID}
                                            onChange={onChangeThingID}
                                            onKeyPress={onKeypress}
                                            maxLength={128}
                                        />
                                        <button type="button" onClick={onSubmitSearchValues}>
                                            <FontAwesomeIcon icon="angle-right" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </Popover.Content>
                    </Popover>
                </Overlay>
            
                <Button onClick={() => setShowBulkModal(true)}>
                    <FontAwesomeIcon icon="search-plus"/>
                </Button>

                <Button onClick={() => setMruOpen(!mruOpen)} ref={mruButtonRef}>
                    <FontAwesomeIcon icon="history"/>
                </Button>

                <Overlay
                    target={mruButtonRef}
                    show={mruOpen}
                    onHide={() => setMruOpen(false)}
                    rootClose
                    placement="bottom"
                >
                    <Popover id="deviceMru_popover" className="searchPopover">
                        <Popover.Content>
                            {MruService.mruDevices.length == 0 && "No MRU devices found"}
                            {MruService.mruDevices.map(d => (
                                <div key={d.uuid} className="mruContainer">
                                    {d.lastRequested > 0 ? new Date(d.lastRequested*1000).toLocaleString() : ""}
                                    <button style={{float: 'right'}} onClick={() => setDevicesFromMru(d)}>
                                        <FontAwesomeIcon icon="search" />
                                    </button>
                                    {d.thingIDs.slice(0, Math.min(3, d.thingIDs.length)).map((t, i) => (
                                        <div key={i}>
                                            - {t}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </Popover.Content>
                    </Popover>
                </Overlay>

                <Button onClick={clearDevices} disabled={devices.length == 0}>
                    <FontAwesomeIcon icon="window-close"/>
                </Button>
                
                <Button onClick={() => pingDevices(selectedDevices)} disabled={selectedDevices.length == 0 || pingDisabled}>
                    Ping
                </Button>
            </ButtonGroup>
        </div>
    );

    const tableHeaders = (devices.length > 0 && (
        <div className="devicesHeaders">
            <div className="devicesHeaders_sorter devicesHeader">
                <input 
                    type="checkbox" 
                    className="devicesItem_check devicesHeaders_selectAll" 
                    onChange={onToggleSelectAll}
                    checked={allSelected}
                />
                <div className="devicesHeaders_selectAllText">
                    {'Select All'}
                </div>
            </div>
            <div className="devicesHeaders_sorter devicesHeader devicesHeaders_makeModel" onClick={sortDevicesByManufacturer}>
                {'Manufacturer'}
                {descending && activeSorter === "manufacturer" && (<FontAwesomeIcon icon="caret-down" className="devicesList_searchCaret" />)}
                {!descending && activeSorter === "manufacturer" && (<FontAwesomeIcon icon="caret-up" className="devicesList_searchCaret" />)}
            </div>
            <div className="devicesHeaders_sorter devicesHeader devicesHeaders_lastSeenDate" onClick={sortDevicesByLastSeen}>
                {'Last Seen'}
                {descending && activeSorter === "lastSeen" && (<FontAwesomeIcon icon="caret-down" className="devicesList_searchCaret" />)}
                {!descending && activeSorter === "lastSeen" && (<FontAwesomeIcon icon="caret-up" className="devicesList_searchCaret" />)}
            </div>
        </div>)
    );

    let content: ReactNode;
    switch (deviceLoadStatus) {
        case DeviceLoadState.Initial:
        case DeviceLoadState.Loading:
        case DeviceLoadState.Success:
            if (devices.length > 0 || deviceLoadStatus === DeviceLoadState.Loading) {
                content = (<>
                    <ul className="devicesList">
                        {devices.map((device) => (
                            <DevicesItem 
                                key={device.id}
                                device={device} 
                                isChecked={isSelected(device.id)}
                                className={deviceItemClassName(device.id)}
                                onPingDevices={pingDevices} 
                                onShowDeviceModal={openDeviceModal} 
                                onSelectDevice={onSelectDevice}
                            />
                        ))}
                    </ul>
                    {deviceLoadStatus === DeviceLoadState.Loading && (
                        <Loader />
                    )}
                </>);
            } else {
                content = (
                    <div className="devicesList_empty">
                        {'No devices found.'}
                    </div>
                );
            }
            break;
        case DeviceLoadState.Error:
            content = (
                <ErrorMessage type={ErrorType.Generic} message={deviceLoadError || 'Error loading devices.'} />
            );
            break;
    }

    return (<>
        <Page>
            <PageHeader title={devices.length === 0 ? 'Devices' : 'Devices (' + devices.length + (devices.length == pageSize ? '+)' : ')')} right={searchHeader}/>
            {tableHeaders}
            {content}
        </Page>
        
        {viewDevice &&  <DeviceModal
            key={viewDevice.id}
            device={viewDevice}
            onHide={closeDeviceModal}
            show={showDeviceModal}
        />}

        {searchModal}
    </>);
}
